// Global context for AniList data and authentication

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getCreativeError } from '../utils/errorMessages';
import { validateToken, fetchUserAnimeList, addAnimeWithRetry } from '../services/aniListService';
import {
    setSimpleCache,
    getSimpleCache,
    clearAllCache
} from '../services/cacheService';
import {
    LOCAL_STORAGE_KEYS,
    CACHE_TTL,
    EXCLUDED_DISPLAY_RELATION_TYPES,
    RELATION_TYPE_INVERSE
} from '../config/constants';
import { migrateToUserSpecificStorage, clearUserData } from '../utils/userStorage';
import {
    serverLogin,
    getSavedComparisons
} from '../services/apiService';
import { signalServerAuthReady, resetAuthReady } from '../services/authReadyService';
import sqliteService from '../services/sqliteService';
import { useTitlePreference } from './TitlePreferenceContext';
import { getDisplayTitle } from '../hooks/useDisplayTitle';

// Helper: filter pending anime BEFORE storing in state
// Strips unwanted relation types that may leak from stale cache data
// NOTE: Removed-anime filtering is handled by the components themselves (NewRelatedAnime + RemovedAnimes)
// Uses the shared EXCLUDED_DISPLAY_RELATION_TYPES from constants.js (single source of truth)

const filterRemovedFromPending = (pending) => {
    if (!pending || pending.length === 0) return pending;

    // Filter out unwanted relation types (safety net for stale cache data)
    const beforeFilter = pending.length;
    const filtered = pending.filter(anime => !EXCLUDED_DISPLAY_RELATION_TYPES.has(anime.relationType));
    if (filtered.length < beforeFilter) {
        console.log(`🚫 Filtered ${beforeFilter - filtered.length} unwanted relation types from pending list`);
    }

    return filtered;
};


const AniListContext = createContext();



export const useAniList = () => {
    const context = useContext(AniListContext);
    if (!context) {
        throw new Error('useAniList must be used within AniListProvider');
    }
    return context;
};

export const AniListProvider = ({ children }) => {
    const [token, setToken] = useState(null);
    const [username, setUsername] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true); // Start true so skeletons show from first render
    const [isRefreshing, setIsRefreshing] = useState(false); // Track manual refresh separately
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Groups and relations state
    const [animeGroups, setAnimeGroups] = useState([]);
    const [pendingAnime, setPendingAnime] = useState([]);
    const [relationsData, setRelationsData] = useState({});
    const [fetchProgress, setFetchProgress] = useState({ progress: 0, status: '' });
    const [isProcessingGroups, setIsProcessingGroups] = useState(false);
    const prevUserDataRef = React.useRef(null); // For change detection (ref to avoid re-triggers)
    const buildLockRef = React.useRef(false); // Prevent concurrent builds
    const hasDisplayedDataRef = React.useRef(false); // Track: true once groups loaded (prevents skeleton re-flash)
    const connectingRef = React.useRef(false); // Guard: true while connectAniList is in progress
    const mountLoadingRef = React.useRef(false);
    const { titlePreference } = useTitlePreference();
    // Guard: true while mount effect is loading from IndexedDB - see mountLoadingRef

    // Saved comparisons state - loaded globally so it's available immediately
    const [savedComparisons, setSavedComparisons] = useState([]);
    const [loadingSavedComparisons, setLoadingSavedComparisons] = useState(false);

    // Initialize from cache on mount
    useEffect(() => {
        let aborted = false; // StrictMode guard: prevents stale IIFE from setting state after unmount

        (async () => {
            mountLoadingRef.current = true; // Signal: mount is loading, don't fetch from API yet
            const cachedToken = getSimpleCache(LOCAL_STORAGE_KEYS.TOKEN);
            const cachedUsername = getSimpleCache(LOCAL_STORAGE_KEYS.USERNAME);

            // If we have a cached username, reconnect
            if (cachedUsername) {
                console.log(`🔄 Auto-reconnecting as "${cachedUsername}" from cache...`);

                if (aborted) return; // StrictMode: first mount was cleaned up
                setToken(cachedToken || null);
                setUsername(cachedUsername);
                setIsConnected(true);

                // Re-authenticate with backend server to sync cached token
                if (cachedToken) {
                    const cachedAnilistId = localStorage.getItem('anilist_user_id');
                    const cachedAvatar = localStorage.getItem('anilist_avatar');
                    if (cachedAnilistId) {
                        serverLogin(cachedToken, cachedUsername, parseInt(cachedAnilistId), cachedAvatar || null)
                            .then(result => {
                                if (result) console.log('📡 Server re-auth successful (auto-reconnect)');
                                else console.warn('⚠️ Server re-auth failed (auto-reconnect)');
                                signalServerAuthReady();
                            })
                            .catch(err => {
                                console.warn('⚠️ Server re-auth error:', err.message);
                                signalServerAuthReady();
                            });
                    } else {
                        console.log('⚠️ No cached anilistId, validating token for server login...');
                        validateToken(cachedToken).then(user => {
                            localStorage.setItem('anilist_user_id', user.id.toString());
                            localStorage.setItem('anilist_avatar', user.avatar?.large || user.avatar?.medium || '');
                            return serverLogin(cachedToken, cachedUsername, user.id, user.avatar?.large || user.avatar?.medium);
                        }).then(result => {
                            if (result) console.log('📡 Server login successful (token validated)');
                            signalServerAuthReady();
                        }).catch(err => {
                            console.warn('⚠️ Token validation failed:', err.message);
                            signalServerAuthReady();
                        });
                    }
                } else {
                    signalServerAuthReady();
                }

                // Load userData AND cached groups from IndexedDB in parallel
                const [cachedUserData, cachedGroups] = await Promise.all([
                    sqliteService.getLargeCache(LOCAL_STORAGE_KEYS.USER_DATA),
                    sqliteService.loadGroupsCache()
                ]);
                if (aborted) return;

                // INSTANT DISPLAY: If we have cached groups, show them NOW (no skeleton)
                // Reject old cache versions (v2 had mislabeled relation types from 'SEQUEL' default)
                const CURRENT_CACHE_VERSION = 3;
                if (cachedGroups && cachedGroups.groups && cachedGroups.groups.length > 0 && (cachedGroups.cache_version || 0) >= CURRENT_CACHE_VERSION) {
                    console.log(`⚡ Instant display: ${cachedGroups.groups.length} cached groups (v${cachedGroups.cache_version})`);
                    setAnimeGroups(cachedGroups.groups);
                    setPendingAnime(filterRemovedFromPending(cachedGroups.pending || []));
                    setRelationsData(cachedGroups.relations || {});
                    hasDisplayedDataRef.current = true;
                } else if (cachedGroups && (cachedGroups.cache_version || 0) < CURRENT_CACHE_VERSION) {
                    console.log(`🔄 Stale cache v${cachedGroups.cache_version || '?'} detected — will rebuild fresh (current: v${CURRENT_CACHE_VERSION})`);
                }

                if (cachedUserData) {
                    console.log('📦 Loaded USER_DATA from IndexedDB cache');
                    const cacheAge = await sqliteService.getLargeCacheAge(LOCAL_STORAGE_KEYS.USER_DATA);
                    if (aborted) return;
                    setUserData(cachedUserData);
                    setLastUpdated(Date.now() - cacheAge);
                    // Groups will auto-rebuild in background via useEffect when userData is set
                } else {
                    console.log('⚠️ No cached USER_DATA in IndexedDB');
                }

                console.log('✅ Auto-reconnect complete - user session restored');
            } else {
                signalServerAuthReady();
                setIsLoading(false);
            }
            if (!aborted) {
                mountLoadingRef.current = false;
                setIsLoading(false);
            }
        })();

        return () => { aborted = true; }; // Cleanup: abort if StrictMode remounts
    }, []);

    // Fetch data from AniList API
    const fetchData = useCallback(async (force = false) => {
        if (!username) {
            setError('Not connected to AniList');
            return;
        }

        console.log(`🔄 fetchData called with force=${force}`);

        // Check cache first (unless forced)
        if (!force) {
            const isValid = await sqliteService.isLargeCacheValid(LOCAL_STORAGE_KEYS.USER_DATA);
            if (isValid) {
                const cachedData = await sqliteService.getLargeCache(LOCAL_STORAGE_KEYS.USER_DATA);
                if (cachedData) {
                    console.log('📦 Using cached USER_DATA from IndexedDB (not forced, cache valid)');
                    const cacheAge = await sqliteService.getLargeCacheAge(LOCAL_STORAGE_KEYS.USER_DATA);
                    setUserData(cachedData);
                    setLastUpdated(Date.now() - cacheAge);
                    return;
                }
            }
        }

        console.log('🌐 Fetching fresh data from AniList API...');
        // Only show skeleton if no cached data is currently displayed
        if (!hasDisplayedDataRef.current) {
            setIsLoading(true);
        }
        setError(null);

        try {
            // Fetch with token if available, otherwise public access
            const data = await fetchUserAnimeList(username, token);

            // Cache the data to IndexedDB (no size limit unlike localStorage)
            const allEntries = data.lists?.flatMap(list => list.entries || []) || [];
            console.log(`💾 Saving USER_DATA to IndexedDB cache: ${allEntries.length} anime`);
            await sqliteService.saveLargeCache(LOCAL_STORAGE_KEYS.USER_DATA, data, CACHE_TTL.USER_DATA);
            console.log('✅ USER_DATA cache saved to IndexedDB successfully');


            console.log('📝 Setting userData state with fresh data');
            setUserData(data);
            setLastUpdated(Date.now());
        } catch (err) {
            console.error('Error fetching user data:', err);
            setError(getCreativeError(err.message || 'Failed to fetch anime list'));

            // Try to load from IndexedDB cache if available
            const cachedData = await sqliteService.getLargeCache(LOCAL_STORAGE_KEYS.USER_DATA);
            if (cachedData) {
                const cacheAge = await sqliteService.getLargeCacheAge(LOCAL_STORAGE_KEYS.USER_DATA);
                setUserData(cachedData);
                setLastUpdated(Date.now() - cacheAge);
            }
        } finally {
            setIsLoading(false);
            setIsRefreshing(false); // Clear refreshing state
        }
    }, [token, username]);

    // Initial data fetch — cache-first architecture
    // For returning users: cache was loaded in mount effect, App.js sync loop handles background refresh
    // For first-time users: no cache exists, so we must fetch from API
    useEffect(() => {
        if (!isConnected || !username) return;

        // Wait for mount effect to finish loading from IndexedDB
        if (mountLoadingRef.current) {
            console.log('⏳ Mount still loading from cache, skipping');
            return;
        }

        // Only fetch from API if we have NO cached data at all (first-time user)
        // AND connectAniList is not already running (it handles its own fetch)
        if (!userData) {
            // connectAniList sets connectingRef — if in progress, it handles its own fetch
            if (connectingRef.current) {
                console.log('🔗 connectAniList in progress — it will handle the fetch');
            } else {
                console.log('📊 No cached data (first-time user) — fetching from API...');
                fetchData(true);
            }
        } else {
            console.log('✅ Cached data loaded — background sync will handle refresh');
            setIsLoading(false);
        }
    }, [isConnected, username, fetchData]);

    // Connect to AniList via OAuth token
    const connectAniList = async (authToken) => {
        console.log(`🔗 Connecting with OAuth token...`);
        setIsLoading(true);
        setError(null);

        // Mark as connecting so other effects don't fire redundant fetches
        connectingRef.current = true;

        // Reset auth ready state so manga/other contexts wait for the NEW server login
        resetAuthReady();

        try {
            if (!authToken) {
                throw new Error('Please provide an access token');
            }

            console.log('🔐 Using OAuth token...');
            const user = await validateToken(authToken);

            setToken(authToken);
            setUsername(user.name);
            setIsConnected(true);

            setSimpleCache(LOCAL_STORAGE_KEYS.TOKEN, authToken);
            setSimpleCache(LOCAL_STORAGE_KEYS.USERNAME, user.name);

            // Cache anilistId and avatar for auto-reconnect server login
            localStorage.setItem('anilist_user_id', user.id.toString());
            localStorage.setItem('anilist_avatar', user.avatar?.large || user.avatar?.medium || '');

            // Login to backend server IMMEDIATELY after token validation
            // This must happen BEFORE the slow anime fetch (~10s for large lists)
            // so manga context's waitForServerAuth() doesn't time out
            try {
                const result = await serverLogin(authToken, user.name, user.id, user.avatar?.large || user.avatar?.medium);
                if (result) {
                    console.log('📡 Server login successful');
                } else {
                    console.warn('⚠️ Server login failed (data will stay local)');
                }
            } catch (err) {
                console.warn('⚠️ Server login error:', err.message);
            }
            signalServerAuthReady();

            // NOW fetch the full anime list (this is the slow part)
            console.log(`📊 Fetching anime list for ${user.name}...`);
            const data = await fetchUserAnimeList(user.name, authToken);
            await sqliteService.saveLargeCache(LOCAL_STORAGE_KEYS.USER_DATA, data, CACHE_TTL.USER_DATA);
            setUserData(data);
            setLastUpdated(Date.now());

            const allEntries = data.lists?.flatMap(l => l.entries || []) || [];
            console.log(`✅ Connected! Found ${allEntries.length} anime`);


            // Migrate existing data to user-specific storage
            migrateToUserSpecificStorage();

            return { success: true };
        } catch (err) {
            console.error('❌ Connection failed:', err);
            setError(getCreativeError(err.message || 'Failed to connect to AniList'));
            return { success: false, error: err.message };
        } finally {
            connectingRef.current = false;
            setIsLoading(false);
        }
    };

    // Disconnect from AniList
    const disconnectAniList = () => {
        // Clear user-specific data first
        clearUserData();

        setToken(null);
        setUsername(null);
        setUserData(null);
        setIsConnected(false);
        setLastUpdated(null);
        setError(null);

        // Clear cached server login info
        localStorage.removeItem('anilist_user_id');
        localStorage.removeItem('anilist_avatar');

        // Reset auth-ready state for next login
        resetAuthReady();

        clearAllCache();
    };

    // Disconnect OAuth only (keep username connection)
    const disconnectOAuth = () => {
        setToken(null);

        // Remove only the token from localStorage, keep username
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);

        console.log('🔓 OAuth disconnected (username connection preserved)');
    };

    // Listen for auth-expired events from the API service layer
    // When a 401 is detected during any mutation, the service dispatches this event
    // This auto-disconnects OAuth so the Settings UI shows "Not Authorized"
    React.useEffect(() => {
        const handleAuthExpired = (event) => {
            console.warn('🔒 Auth expired event received:', event.detail?.message);
            // Clear token state so Settings UI switches to "OAuth Not Connected"
            setToken(null);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
        };

        window.addEventListener('auth-expired', handleAuthExpired);
        return () => window.removeEventListener('auth-expired', handleAuthExpired);
    }, []);

    // Load saved comparisons from server
    const loadSavedComparisons = useCallback(async () => {
        if (!username) return;
        setLoadingSavedComparisons(true);
        try {
            const result = await getSavedComparisons(username);
            setSavedComparisons(result?.comparisons || []);
        } catch (err) {
            console.error('Failed to load saved comparisons:', err);
            setSavedComparisons([]);
        } finally {
            setLoadingSavedComparisons(false);
        }
    }, [username]);

    // Load saved comparisons when user connects
    useEffect(() => {
        if (isConnected && username) {
            loadSavedComparisons();
        }
    }, [isConnected, username, loadSavedComparisons]);

    // ============================================================
    // HYBRID CLUSTER-BASED GROUP BUILDING
    // Replaces the old fetchAndBuildGroups + performIncrementalUpdate
    // Simply sends user's anime IDs to server, gets pre-computed clusters
    // ============================================================
    const buildGroupsFromClusters = useCallback(async () => {
        if (!userData || !userData.lists) {
            console.warn('No user data available to build groups');
            return;
        }

        if (buildLockRef.current) {
            console.log('⏭️ Build already in progress, skipping');
            return;
        }

        buildLockRef.current = true;
        // Only show progress/loading if no cached data is already displayed
        const isBackgroundRebuild = hasDisplayedDataRef.current;
        if (!isBackgroundRebuild) {
            setIsProcessingGroups(true);
            setFetchProgress({ progress: 10, status: 'Fetching pre-computed clusters...' });
        } else {
            console.log('🔄 Background rebuild — cached data already displayed');
        }

        try {
            const allEntries = userData.lists.flatMap(list => list.entries || []);
            const mediaIds = allEntries.map(entry => entry.media?.id).filter(Boolean);
            // titlePreference from reactive hook (was localStorage)

            console.log(`📊 Building groups from clusters for ${mediaIds.length} anime...`);

            // Fetch pre-computed clusters from server
            const { getClustersForAnime } = await import('../services/apiService');
            const result = await getClustersForAnime(mediaIds);

            if (!result || !result.clusters) {
                console.warn('⚠️ Cluster fetch failed, falling back to server group build');
                // Fallback: use the old server-side group builder
                const { buildGroupsOnServer } = await import('../services/apiService');
                const fallback = await buildGroupsOnServer(allEntries, titlePreference);
                if (fallback && fallback.groups) {
                    setAnimeGroups(fallback.groups);
                    setPendingAnime(filterRemovedFromPending(fallback.pending || []));
                    setRelationsData(fallback.relationsData || {});
                    hasDisplayedDataRef.current = true;
                }
                setFetchProgress({ progress: 100, status: 'Groups built (fallback)' });
                return;
            }

            if (!isBackgroundRebuild) setFetchProgress({ progress: 50, status: 'Processing clusters...' });

            // Build entry lookup map
            const entryMap = {};
            allEntries.forEach(entry => {
                if (entry.media?.id) entryMap[entry.media.id] = entry;
            });

            const userIdSet = new Set(mediaIds);
            const { getDisplayTitle, calculateGroupStats, sortGroupByReleaseDate } = await import('../services/groupingService');

            // Convert clusters to the group format the UI expects
            const groups = [];
            const pendingAnimeList = [];
            const relData = {};

            for (const [groupId, cluster] of Object.entries(result.clusters)) {
                const { animeIds, catalogData, relations } = cluster;

                // Separate: anime in user's list vs not in user's list
                const userAnimeInCluster = animeIds.filter(id => userIdSet.has(id));
                const nonUserAnimeInCluster = animeIds.filter(id => !userIdSet.has(id));

                if (userAnimeInCluster.length === 0) continue; // Skip clusters with no user anime

                // Build group entries from user's entries
                const groupEntries = userAnimeInCluster
                    .map(id => entryMap[id])
                    .filter(Boolean);

                if (groupEntries.length === 0) continue;

                // Sort by release date and calculate stats
                const sorted = sortGroupByReleaseDate(groupEntries);
                const stats = calculateGroupStats(sorted);
                const groupName = getDisplayTitle(sorted[0].media, titlePreference);

                groups.push({
                    id: `cluster-${groupId}`,
                    name: groupName,
                    anime: sorted,
                    stats,
                    firstReleaseDate: sorted[0]?.media?.startDate || null,
                    status: sorted.every(e => e.status === 'COMPLETED') ? 'COMPLETED' : 'IN_PROGRESS'
                });

                // Detect pending anime (related anime not in user's list)
                for (const nonUserId of nonUserAnimeInCluster) {
                    const catData = catalogData?.[nonUserId];
                    if (!catData) continue;

                    // Skip if format is excluded (MUSIC, etc.)
                    if (catData.format === 'MUSIC') continue;

                    // Find which user anime is related to this pending anime
                    let relationType = 'RELATED'; // safe default
                    let sourceAnimeId = null;

                    // Check 1 (preferred): Look at relations FROM user anime TO pending anime
                    // This gives the correct perspective: what the pending anime IS to the user's anime
                    for (const userId of userAnimeInCluster) {
                        if (relations?.[userId]) {
                            const edges = relations[userId]?.relations?.edges || [];
                            for (const edge of edges) {
                                if (edge.node?.id === nonUserId) {
                                    relationType = edge.relationType;
                                    sourceAnimeId = userId;
                                    break;
                                }
                            }
                        }
                        if (sourceAnimeId) break;
                    }

                    // Check 2 (fallback): Look at relations FROM pending anime TO user anime
                    // This gives the INVERTED perspective, so we invert the type
                    if (!sourceAnimeId) {
                        if (relations?.[nonUserId]) {
                            const edges = relations[nonUserId]?.relations?.edges || [];
                            for (const edge of edges) {
                                if (userIdSet.has(edge.node?.id)) {
                                    // Invert: edge says "user anime is X of pending" → we want "pending is Y of user anime"
                                    relationType = RELATION_TYPE_INVERSE[edge.relationType] || edge.relationType;
                                    sourceAnimeId = edge.node.id;
                                    break;
                                }
                            }
                        }
                    }

                    // Skip excluded relation types (uses shared constant)
                    if (EXCLUDED_DISPLAY_RELATION_TYPES.has(relationType)) continue;

                    // Skip orphaned anime: no direct non-excluded connection found
                    // These only connect through banned types (SUMMARY/COMPILATION intermediates)
                    if (relationType === 'RELATED' && !sourceAnimeId) continue;

                    const sourceEntry = sourceAnimeId ? entryMap[sourceAnimeId] : null;

                    pendingAnimeList.push({
                        id: nonUserId,
                        title: catData.title,
                        type: catData.type,
                        format: catData.format,
                        status: catData.status,
                        episodes: catData.episodes,
                        chapters: catData.chapters,
                        volumes: catData.volumes,
                        duration: catData.duration,
                        startDate: catData.startDate,
                        coverImage: catData.coverImage,
                        averageScore: catData.averageScore,
                        description: catData.description,
                        relationType,
                        relatedTo: sourceEntry?.media ? {
                            id: sourceEntry.media.id,
                            title: getDisplayTitle(sourceEntry.media, titlePreference)
                        } : null,
                        sourceAnimeId,
                        clusterGroupId: groupId
                    });
                }

                // Store relations data for this cluster
                if (relations) {
                    Object.assign(relData, relations);
                }
            }

            // Deduplicate pending anime by ID
            const seenPending = new Set();
            const uniquePending = pendingAnimeList.filter(p => {
                if (seenPending.has(p.id)) return false;
                seenPending.add(p.id);
                return true;
            });

            if (!isBackgroundRebuild) setFetchProgress({ progress: 90, status: 'Finalizing...' });

            // Set state
            setAnimeGroups(groups);
            setPendingAnime(filterRemovedFromPending(uniquePending));
            setRelationsData(relData);
            hasDisplayedDataRef.current = true;

            // OPTIMIZATION 1: Cache built groups to IndexedDB for instant display on next visit
            try {
                await sqliteService.saveGroupsCache({
                    groups,
                    pending: uniquePending,
                    relations: relData,
                    animeCount: mediaIds.length,
                    cache_version: 5, // v5: filter orphaned anime (no direct non-excluded connection)
                    timestamp: Date.now()
                });
                console.log(`💾 Groups cached: ${groups.length} groups for instant display on next visit`);
            } catch (cacheErr) {
                console.warn('⚠️ Failed to cache groups:', cacheErr.message);
            }

            if (!isBackgroundRebuild) setFetchProgress({ progress: 100, status: `${groups.length} groups built from clusters!` });
            console.log(`✅ Cluster-based build complete: ${groups.length} groups, ${uniquePending.length} pending from ${mediaIds.length} anime`);

            // Cleanup stale removed anime IDs (background, fire-and-forget)
            import('../services/removedAnimeService').then(({ cleanupStaleRemovedAnime }) => {
                cleanupStaleRemovedAnime(userIdSet);
            }).catch(err => console.warn('⚠️ Stale cleanup failed:', err.message));

        } catch (err) {
            console.error('❌ Error building groups from clusters:', err);
            setError(getCreativeError(err.message || 'Failed to build groups'));
        } finally {
            if (!isBackgroundRebuild) setIsProcessingGroups(false);
            buildLockRef.current = false;
        }
    }, [userData]);

    // Alias for backward compatibility (used by clearGroupsCache and context value)
    const fetchAndBuildGroups = buildGroupsFromClusters;

    // Auto-build groups when userData changes
    useEffect(() => {
        if (userData && userData.lists) {
            const prevData = prevUserDataRef.current;

            if (!prevData || prevData !== userData) {
                console.log('🎯 userData changed — rebuilding groups from clusters');
                buildGroupsFromClusters();
            }

            prevUserDataRef.current = userData;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    // Clear groups cache and rebuild from clusters
    const clearGroupsCache = useCallback(() => {
        console.log('🔄 Force rebuild — re-fetching clusters from server...');
        setIsProcessingGroups(true);
        setFetchProgress({ progress: 0, status: 'Starting force rebuild...' });
        if (userData && userData.lists) {
            buildGroupsFromClusters();
        }
    }, [userData, buildGroupsFromClusters]);

    // Add pending anime to user's list
    const addPendingAnimeToList = useCallback(async (mediaId, status = 'PLANNING', skipRefresh = false) => {
        if (!token) {
            throw new Error('OAuth token required to add anime. Please reconnect with OAuth.');
        }

        try {
            // Add to AniList with retry logic
            const result = await addAnimeWithRetry(mediaId, status, token);

            // ONLY remove from pending AFTER successful add (not before!)
            // This ensures cancelled/failed items stay visible in the Related tab
            setPendingAnime(prev => prev.filter(anime => anime.id !== mediaId));

            // Only trigger refresh if NOT in batch mode
            if (!skipRefresh) {
                console.log(`🔄 Triggering immediate refresh for anime ${mediaId}`);
                fetchData(true).then(() => {
                    console.log(`✅ Immediate refresh complete`);
                }).catch(err => {
                    console.error(`❌ Immediate refresh failed:`, err);
                });
            } else {
                console.log(`⏭️ Skipping refresh for batch mode (anime ${mediaId})`);
            }

            console.log(`✅ Added anime ${mediaId} successfully`);

            return result;
        } catch (err) {
            console.error('Error adding anime to list:', err);
            throw err;
        }
    }, [token, fetchData]);





    // Manual refresh with incremental update support
    const refreshData = useCallback(async (force = true) => {
        console.log(`⏱ MANUAL REFRESH triggered (force=${force})`);
        setIsRefreshing(true); // Set refreshing state
        // Fetch new data - incremental update will be triggered by useEffect automatically
        await fetchData(force);
        console.log('✅ MANUAL REFRESH complete');
    }, [fetchData]);

    // Direct data update from sync service (skips redundant AniList API call)
    // skipSave: if true, skip IndexedDB/server saves (used for periodic rebuilds with no actual changes)
    const updateUserDataDirect = useCallback(async (syncedData, skipSave = false) => {
        console.log(`⚡ Direct userData update from sync (skipSave=${skipSave})`);
        const data = { lists: syncedData.lists };

        if (!skipSave) {
            // Cache the data to IndexedDB (no size limit)
            const allEntries = data.lists?.flatMap(list => list.entries || []) || [];
            console.log(`💾 Caching synced data to IndexedDB: ${allEntries.length} anime`);
            await sqliteService.saveLargeCache(LOCAL_STORAGE_KEYS.USER_DATA, data, CACHE_TTL.USER_DATA);
            console.log('✅ Synced data cached to IndexedDB');

        } else {
            console.log('⏭️ Skipping IndexedDB/server saves (periodic rebuild, no data changes)');
        }

        // Update state — this triggers the useEffect that handles incremental updates
        setUserData(data);
        setLastUpdated(Date.now());
        setIsRefreshing(false);
    }, [token]);

    // Derived reactive groups/pending (instant Title Language toggle without refresh)
    const displayedAnimeGroups = React.useMemo(() => {
        if (!animeGroups || animeGroups.length === 0) return animeGroups;
        return animeGroups.map(group => {
            const media = group.anime?.[0]?.media;
            if (!media) return group;
            const newName = getDisplayTitle(media, titlePreference);
            return newName === group.name ? group : { ...group, name: newName };
        });
    }, [animeGroups, titlePreference]);
    const displayedPendingAnime = React.useMemo(() => {
        if (!pendingAnime || pendingAnime.length === 0) return pendingAnime;
        const mediaMap = new Map();
        if (userData?.lists) userData.lists.flatMap(l => l.entries || []).forEach(e => { if (e.media?.id) mediaMap.set(e.media.id, e.media); });
        animeGroups.forEach(g => { (g.anime || []).forEach(entry => { if (entry.media?.id) mediaMap.set(entry.media.id, entry.media); }); });
        return pendingAnime.map(p => {
            if (!p.relatedTo || p.sourceAnimeId == null) return p;
            const srcMedia = mediaMap.get(p.sourceAnimeId) || mediaMap.get(p.relatedTo.id);
            if (!srcMedia) return p;
            const nt = getDisplayTitle(srcMedia, titlePreference);
            if (nt === p.relatedTo.title) return p;
            return { ...p, relatedTo: { ...p.relatedTo, title: nt } };
        });
    }, [pendingAnime, animeGroups, userData, titlePreference]);
    const value = {
        token,
        username,
        userData,
        isConnected,
        isLoading,
        isRefreshing, // Export refreshing state
        error,
        lastUpdated,
        connectAniList,
        disconnectAniList,
        disconnectOAuth,
        refreshData,
        updateUserDataDirect,
        // Groups and relations
        animeGroups: displayedAnimeGroups,
        pendingAnime: displayedPendingAnime,
        relationsData,
        fetchProgress,
        isProcessingGroups,
        fetchAndBuildGroups,
        addPendingAnimeToList,
        clearGroupsCache,
        // Saved comparisons (loaded globally for instant access)
        savedComparisons,
        loadingSavedComparisons,
        loadSavedComparisons
    };

    return (
        <AniListContext.Provider value={value}>
            {children}
        </AniListContext.Provider>
    );
};
